import React from 'react';
import { WizardStep } from '@app/app/_components/Wizard/Wizard';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { StepperList } from '@app/app/_components/StepperList/StepperList';
import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseLifeInsuranceResult } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { Message } from '@app/app/_components/Message/Message';
import { useTranslations } from 'next-intl';
import { ChoosePackageStep } from '@app/app/esales/life/_sections/steps/ChoosePackageStep';
import { LifePackageCode } from '@app/domain/model/eshop/life/LifePackageCode';
import { CustomCoverSelectionStep } from '@app/app/esales/life/_sections/steps/CustomCoverSelectionStep/CustomCoverSelectionStep';
import { PaymentScheduleStep } from '@app/app/esales/life/_sections/steps/PaymentScheduleStep/PaymentScheduleStep';
import { CheckIcon } from '@app/app/_icons/CheckIcon';
import { LifeCalculateResult } from '@app/domain/model/eshop/life/LifeCalculateResult';
import { ProfessionStep } from '@app/app/esales/life/_sections/steps/ProfessionStep/ProfessionStep';
import { CommonType } from '@app/domain/model/CommonType';
import { HobbiesStep } from '@app/app/esales/life/_sections/steps/HobbiesStep/HobbiesStep';
import { TransportationMeansStep } from '@app/app/esales/life/_sections/steps/TransportationMeansStep/TransportationMeansStep';
import { LifeProducts } from '@app/domain/model/eshop/life/LifeProducts';
import { Card } from '@app/app/_components/Card/Card';
import { Heading } from '@app/app/_components/Heading/Heading';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { TaxInformationStep } from '@app/app/esales/life/_sections/steps/TaxInformationStep/TaxInformationStep';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { ContactDetailsStep } from '@app/app/esales/life/_sections/steps/ContactDetailsStep/ContactDetailsStep';
import { LIFE_INSURANCE_STEPS } from '@app/app/esales/life/_sections/steps/LIFE_INSURANCE_STEPS';
import { OfferSummaryStep } from '@app/app/esales/life/_sections/steps/OfferSummaryStep/OfferSummaryStep';
import { HealthQuestionnaireStep } from '@app/app/esales/life/_sections/steps/HealthQuestionnaireStep/HealthQuestionnaireStep';
import { BeneficiariesStep } from '@app/app/self-service/(wizard)/change-beneficiary-death/[id]/_sections/steps/BeneficiariesStep/BeneficiariesStep';
import { useDeathBeneficiaryManagement } from '@app/app/self-service/(wizard)/change-beneficiary-death/[id]/hooks/useDeathBeneficiaryManagement';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { LifeInsuredPerson } from '@app/domain/model/LifeInsuredPerson';
import { EInvoiceStep } from '@app/app/esales/life/_sections/steps/EInvoiceStep';
import { OverviewContractStep } from '@app/app/esales/life/_sections/steps/OverviewContractStep/OverviewContractStep';
import { useEditController } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/hooks/useEditController';
import { ConsentsStep } from '@app/app/esales/life/_sections/steps/ConsentsStep';
import { PaymentStep } from '@app/app/esales/life/_sections/steps/PaymentStep';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';
import { useLifeInsuranceWizardCallbacks } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/hooks/useLifeInsuranceWizardCallbacks';
import { DateOfBirthStep } from '@app/app/esales/life/_sections/steps/DateOfBirthStep/DateOfBirthStep';
import { InsuranceDurationStep } from '@app/app/esales/life/_sections/steps/InsuranceDurationStep/InsuranceDurationStep';
import { LifePackage } from '@app/domain/model/eshop/life/LifePackage';
import { Tenant } from '@app/infrastructure/tenant/model/Tenant';

interface Props {
    translations: ReturnType<typeof useTranslations>;
    formState: FormState<PurchaseLifeInsuranceResult>;
    lifeProducts: LifeProducts;
    handleRecalculation: (scheduleToCalculate?: string) => Promise<Nullable<LifeCalculateResult>>;
    authState: AuthState;
    editController: ReturnType<typeof useEditController>;
    allHealthAnswersAreValid: boolean;
    isTaxInformationValid: boolean;
    selectedPackage: Nullable<LifePackage>;
    beneficiaryManagement: ReturnType<typeof useDeathBeneficiaryManagement>;
    selectedInsuredPerson: Nullable<LifeInsuredPerson>;
    sessionData: Nullable<SessionResponse>;
    lifeInsuranceWizardCallbacks: ReturnType<typeof useLifeInsuranceWizardCallbacks>;
    packagesVersion: number;
    dateOfBirth: string;
    onDateOfBirthChange: (dob: string) => void;
    tenant: Tenant;
}

export function buildSteps({
    translations,
    formState,
    lifeProducts,
    handleRecalculation,
    authState,
    editController,
    allHealthAnswersAreValid,
    isTaxInformationValid,
    beneficiaryManagement,
    selectedInsuredPerson,
    selectedPackage,
    sessionData,
    lifeInsuranceWizardCallbacks,
    packagesVersion,
    dateOfBirth,
    onDateOfBirthChange,
    tenant,
}: Props): WizardStep[] {
    const t = translations;

    const handleEditDefensesFromHealthSummary = () =>
        editController.handleEditDefenses?.(LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS_SUMMARY);

    const handleEditContractInfoFromHealthSummary = () =>
        editController.handleEditContractInfo?.(LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS_SUMMARY);

    const handleEditDefensesFromTaxSummary = () =>
        editController.handleEditDefenses?.(LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION_SUMMARY);

    const handleEditContractInfoFromTaxSummary = () =>
        editController.handleEditContractInfo?.(LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION_SUMMARY);

    const handleEditDefensesFromSummary = () => editController.handleEditDefenses?.(LIFE_INSURANCE_STEPS.SUMMARY);

    const handleEditContractInfoFromSummary = () =>
        editController.handleEditContractInfo?.(LIFE_INSURANCE_STEPS.SUMMARY);

    const handleHaveSportsHobbyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        lifeInsuranceWizardCallbacks.handleHaveSportsHobbyChange(e.target.value);
    };

    return [
        {
            label: t('eshop.life.start'),
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.START,
                    title: t('eshop.life.howDoesItWork'),
                    body: (
                        <>
                            <Paragraph className="mb-8">{t('eshop.life.howDoesItWorkDescription')}</Paragraph>
                            <StepperList
                                variant="outlined"
                                color="primaryDark"
                                steps={[
                                    {
                                        label: <Paragraph>{t('eshop.life.wizardSteps.packageAndCoverages')}</Paragraph>,
                                        details: <></>,
                                    },
                                    {
                                        label: (
                                            <Paragraph>
                                                {t('eshop.life.wizardSteps.lifestyleAndHealthQuestions')}
                                            </Paragraph>
                                        ),
                                        details: <></>,
                                    },
                                    {
                                        label: <Paragraph>{t('eshop.life.wizardSteps.overview')}</Paragraph>,
                                        details: <></>,
                                    },
                                    {
                                        label: (
                                            <Paragraph>{t('eshop.life.wizardSteps.contractAndFirstPayment')}</Paragraph>
                                        ),
                                        details: <></>,
                                    },
                                ]}
                            />
                        </>
                    ),
                },
            ],
        },
        {
            label: t('eshop.life.wizardSteps.packageAndCoverages'),
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.MAIN_REASON,
                    title: t('eshop.life.mainReasonForBuying'),
                    body: (
                        <ChoiceButtonInput
                            type="checkbox"
                            name="mainReasonForBuying"
                            error={formState.violations?.mainReasonForBuying}
                            options={lifeProducts.reasons.map(reason => ({
                                value: reason.code,
                                label: <div dangerouslySetInnerHTML={{ __html: reason.value }} />,
                            }))}
                            defaultChecked={sessionData?.firstStep?.mainReasonsForBuying}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.AGE,
                    title: t('eshop.life.whatIsYourDateOfBirth'),
                    body: (
                        <DateOfBirthStep
                            error={formState.violations?.dateOfBirth}
                            value={dateOfBirth}
                            onChange={onDateOfBirthChange}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.INCOME,
                    title: t('eshop.life.monthlyCrossIncome'),
                    body: (
                        <ChoiceButtonInput
                            name="monthlyIncome"
                            error={formState.violations?.monthlyIncome}
                            options={lifeProducts.income.map(incomeEntry => ({
                                value: incomeEntry.code,
                                label: `${incomeEntry.value} €`,
                            }))}
                            defaultChecked={sessionData?.firstStep?.monthlyIncome}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CHOOSE_PACKAGE,
                    title: t('eshop.life.choosePackage'),
                    body: (
                        <ChoosePackageStep
                            packages={formState.data?.packages ?? []}
                            sessionData={sessionData}
                            onPackageChange={handleRecalculation}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CUSTOM_COVERS,
                    when: () => formState.data?.formData.selectedPackage === LifePackageCode.Free,
                    title: t('eshop.life.makeYourself'),
                    body: (
                        <CustomCoverSelectionStep
                            key={packagesVersion}
                            covers={selectedPackage?.covers ?? []}
                            onCoverChange={handleRecalculation}
                            sessionData={sessionData}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.DURATION,
                    title: t('eshop.life.forHowManyYearsInsurance'),
                    body: (
                        <InsuranceDurationStep
                            dateOfBirth={dateOfBirth || null}
                            currentValue={formState.data?.formData.insuranceDurationYears}
                            sessionData={sessionData}
                            onRangeChange={handleRecalculation}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.PAYMENT_SCHEDULE,
                    title: t('eshop.life.choosePaymentSchedule'),
                    body: (
                        <PaymentScheduleStep
                            calculation={formState.data?.calculation ?? null}
                            onPaymentScheduleChange={handleRecalculation}
                            sessionData={sessionData}
                        />
                    ),
                },
            ],
        },
        {
            label: t('eshop.life.packagesAndCoveragesDone'),
            hideStepLabel: true,
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.PACKAGES_DONE,
                    title: t('eshop.life.encouragingSentence'),
                    body: (
                        <>
                            <StepperList
                                variant="outlined"
                                color="primaryDark"
                                steps={[
                                    {
                                        label: <Paragraph>{t('eshop.life.wizardSteps.packageAndCoverages')}</Paragraph>,
                                        details: <></>,
                                        variant: 'filled',
                                        color: 'primary',
                                        indexIcon: <CheckIcon className="size-4" />,
                                    },
                                    {
                                        label: (
                                            <Paragraph>
                                                {t('eshop.life.wizardSteps.lifestyleAndHealthQuestions')}
                                            </Paragraph>
                                        ),
                                        details: <></>,
                                    },
                                    {
                                        label: <Paragraph>{t('eshop.life.wizardSteps.overview')}</Paragraph>,
                                        details: <></>,
                                    },
                                    {
                                        label: (
                                            <Paragraph>{t('eshop.life.wizardSteps.contractAndFirstPayment')}</Paragraph>
                                        ),
                                        details: <></>,
                                    },
                                ]}
                            />
                        </>
                    ),
                },
            ],
        },
        {
            label: t('eshop.life.wizardSteps.lifestyleAndHealth'),
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.PROFESSION,
                    title: t('eshop.life.whatIsYourProfession'),
                    body: (
                        <ProfessionStep
                            errorMessage={formState.violations?.profession}
                            jobs={lifeProducts.jobs}
                            defaultValue={sessionData?.secondStep?.jobs ?? undefined}
                            onProfessionChange={handleRecalculation}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HAS_HOBBIES,
                    title: t('eshop.life.doYouHaveHobbies'),
                    body: (
                        <div className="flex flex-col gap-4">
                            <ChoiceButtonInput
                                name="hasSportsHobbies"
                                error={formState.violations?.hasSportsHobbies}
                                options={[
                                    {
                                        value: CommonType.False,
                                        label: t('eshop.life.iDontDoSportOnlyWalk'),
                                    },
                                    {
                                        value: CommonType.True,
                                        label: t('eshop.life.yesIHaveSportHobbies'),
                                    },
                                ]}
                                onChange={handleHaveSportsHobbyChange}
                            />
                            <Message variant="info" className="font-normal">
                                {t('eshop.life.hasSportsHobbiesMessage')}
                            </Message>
                            {lifeInsuranceWizardCallbacks.hasSportHobbies && (
                                <HobbiesStep
                                    errorMessage={formState.violations?.hobbies}
                                    hobbies={lifeProducts.hobbies}
                                    defaultValue={sessionData?.secondStep?.hobbies ?? undefined}
                                    onHobbiesChange={handleRecalculation}
                                />
                            )}
                        </div>
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.TRANSPORTATION,
                    title: t('eshop.life.whatAreYourTransportationMeans'),
                    body: (
                        <TransportationMeansStep
                            transportationMeans={lifeProducts.transportationMeans}
                            error={formState.violations?.transportationMeans}
                            defaultChecked={sessionData?.secondStep?.transportationMeans}
                            onTransportationMeansChange={handleRecalculation}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HALFWAY_LOGIN,
                    when: () => !editController.isEditMode,
                    title: t('eshop.life.youAreHalfwayThere'),
                    body: (
                        <Card className="flex-col items-center justify-center gap-4 px-3 py-8 text-center">
                            <Paragraph isBold className="text-lg">
                                {t('eshop.life.loginStepCardDescription')}
                            </Paragraph>
                            <Paragraph isBold className="text-lg">
                                {t('eshop.life.loginStepCardTitle')}
                            </Paragraph>
                            <Heading level="h1" className="text-primary-500">
                                {formatEuroValue(formState.data?.calculation?.premium ?? 0, tenant)}*
                            </Heading>
                            <Paragraph size="small">*{t('eshop.life.loginStepCardExplanation')}</Paragraph>
                        </Card>
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.CONTACT_DETAILS,
                    when: () => authState.authenticated,
                    title: t('eshop.general.contactDetails'),
                    body: (
                        <ContactDetailsStep
                            authState={authState ?? null}
                            errors={formState.violations}
                            sessionData={sessionData}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS,
                    when: () => authState.authenticated,
                    title: t('eshop.life.myHealthInformation'),
                    body: <HealthQuestionnaireStep errors={formState.violations} sessionData={sessionData} />,
                },
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS_SUMMARY,
                    when: () => authState.authenticated && !allHealthAnswersAreValid,
                    title: t('eshop.life.healthSummaryTitle'),
                    body: (
                        <>
                            {formState.data && (
                                <OfferSummaryStep
                                    packages={formState.data.packages ?? []}
                                    formData={formState.data.formData}
                                    premium={formState.data.calculation?.premium ?? null}
                                    onEditDefenses={handleEditDefensesFromHealthSummary}
                                    onEditContractInfo={handleEditContractInfoFromHealthSummary}
                                    calculation={formState.data.calculation ?? null}
                                />
                            )}
                        </>
                    ),
                },
            ],
        },
        {
            label: t('eshop.life.lifestyleAndHealthDone'),
            hideStepLabel: true,
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH_DONE,
                    title: t('eshop.life.lifestyleAndHealthTitle'),
                    when: () => allHealthAnswersAreValid,
                    body: (
                        <>
                            <StepperList
                                variant="outlined"
                                color="primaryDark"
                                steps={[
                                    {
                                        label: <Paragraph>{t('eshop.life.wizardSteps.packageAndCoverages')}</Paragraph>,
                                        details: <></>,
                                        variant: 'filled',
                                        color: 'primary',
                                        indexIcon: <CheckIcon className="size-4" />,
                                    },
                                    {
                                        label: (
                                            <Paragraph>
                                                {t('eshop.life.wizardSteps.lifestyleAndHealthQuestions')}
                                            </Paragraph>
                                        ),
                                        details: <></>,
                                        variant: 'filled',
                                        color: 'primary',
                                        indexIcon: <CheckIcon className="size-4" />,
                                    },
                                    {
                                        label: <Paragraph>{t('eshop.life.wizardSteps.overview')}</Paragraph>,
                                        details: <></>,
                                    },
                                    {
                                        label: (
                                            <Paragraph>{t('eshop.life.wizardSteps.contractAndFirstPayment')}</Paragraph>
                                        ),
                                        details: <></>,
                                    },
                                ]}
                            />
                        </>
                    ),
                },
            ],
        },
        {
            label: t('eshop.life.reviewingOptions'),
            hideStepLabel: true,
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.SUMMARY,
                    title: t('eshop.life.summaryTitle'),
                    body: (
                        <>
                            {formState.data && (
                                <OfferSummaryStep
                                    packages={formState.data.packages ?? []}
                                    formData={formState.data.formData}
                                    premium={formState.data.calculation?.premium ?? null}
                                    onEditDefenses={handleEditDefensesFromSummary}
                                    onEditContractInfo={handleEditContractInfoFromSummary}
                                    calculation={formState.data.calculation ?? null}
                                />
                            )}
                        </>
                    ),
                },
            ],
        },
        {
            label: t('eshop.life.signingTheContract'),
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION,
                    title: t('eshop.life.taxInformationTitle'),
                    body: (
                        <TaxInformationStep
                            violations={formState.violations}
                            lifeProducts={lifeProducts}
                            sessionData={sessionData}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION_SUMMARY,
                    when: () => isTaxInformationValid === false,
                    title: t('eshop.life.taxInformationSummaryTitle'),
                    body: (
                        <>
                            {formState.data && (
                                <OfferSummaryStep
                                    packages={formState.data.packages ?? []}
                                    formData={formState.data.formData}
                                    premium={formState.data.calculation?.premium ?? null}
                                    onEditDefenses={handleEditDefensesFromTaxSummary}
                                    onEditContractInfo={handleEditContractInfoFromTaxSummary}
                                    calculation={formState.data.calculation ?? null}
                                />
                            )}
                        </>
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.BENEFICIARIES,
                    title: t('eshop.life.beneficiariesStepTitle'),
                    body: (
                        <div className="flex flex-col gap-4">
                            {formState.violations?.beneficiaries && (
                                <Message variant="danger">
                                    {isViolationMessage(formState.violations.beneficiaries) &&
                                    formState.violations.beneficiaries.message ===
                                        'eshop.life.validation.beneficiaryPersonalCodeMatchesProfile'
                                        ? t('eshop.life.validation.beneficiaryPersonalCodeMatchesProfile')
                                        : t('eshop.life.validation.beneficiaryShareError')}
                                </Message>
                            )}
                            <BeneficiariesStep
                                selectedInsuredPerson={selectedInsuredPerson}
                                isMinor={beneficiaryManagement.isMinor}
                                legalHeirsDefault={beneficiaryManagement.legalHeirsDefault}
                                isLegalHeirSelected={beneficiaryManagement.isLegalHeirSelected}
                                handleLegalHeirChange={beneficiaryManagement.handleLegalHeirChange}
                                isEditingBeneficiary={beneficiaryManagement.isEditingBeneficiary}
                                existingBeneficiaries={beneficiaryManagement.existingBeneficiaries}
                                newBeneficiaries={beneficiaryManagement.newBeneficiaries}
                                beneficiaryFormState={beneficiaryManagement.beneficiaryFormState}
                                isCombinedShareFull={beneficiaryManagement.isCombinedShareFull}
                                canAddMoreBeneficiaries={beneficiaryManagement.canAddMoreBeneficiaries}
                                handleAddBeneficiary={beneficiaryManagement.handleAddBeneficiary}
                                editingBeneficiary={beneficiaryManagement.editingBeneficiary}
                                saveAttempted={beneficiaryManagement.saveAttempted}
                                editingBeneficiaryId={beneficiaryManagement.editingBeneficiaryId}
                                handleEditBeneficiary={beneficiaryManagement.handleEditBeneficiary}
                                isEstonianEshop
                                handleSetEditingBeneficiary={beneficiaryManagement.handleSetEditingBeneficiary}
                                countryOptions={[]}
                                isLv={false}
                            />
                        </div>
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.INVOICE_INFORMATION,
                    title: t('eshop.life.invoiceInformationStepTitle'),
                    body: <EInvoiceStep violations={formState.violations} sessionData={sessionData} />,
                },
                {
                    key: LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.OVERVIEW_OF_CONTRACT,
                    title: t('eshop.life.contractSummaryStepTitle'),
                    body: (
                        <OverviewContractStep
                            formState={formState}
                            authState={authState}
                            lifeProducts={lifeProducts}
                            editController={editController}
                        />
                    ),
                },
                {
                    key: LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.CONSENTS,
                    title: t('eshop.life.consentsStepTitle'),
                    body: (
                        <ConsentsStep
                            authState={authState}
                            violations={formState.violations}
                            offerPdfIdentifier={formState.data?.offer?.offerId ?? ''}
                        />
                    ),
                },
            ],
        },
        {
            label: t('eshop.life.paymentStepTitle'),
            subSteps: [
                {
                    key: LIFE_INSURANCE_STEPS.PAYMENT,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentStep paymentMethods={formState.data?.paymentMethods ?? []} />,
                },
            ],
        },
    ];
}
